import { getSharedDriveCapabilities } from "@workith/shared";
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@workith/ui";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  HardDrive,
  Link as LinkIcon,
  PanelLeft,
  PanelLeftClose,
  Plus,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SharedDriveCreateDialog } from "../shared-drive/SharedDriveCreateDialog";
import { SharedDriveMembersDialog } from "../shared-drive/SharedDriveMembersDialog";
import { SidebarDocumentPreviewList, SidebarFolderTree } from "./SidebarFolderTree";
import {
  readExpandedSidebarNodeIds,
  readSidebarCollapsed,
  writeExpandedSidebarNodeIds,
  writeSidebarCollapsed,
} from "./sidebarTreeStorage";
import { useSidebarTree } from "./useSidebarTree";

const menuItems = [
  { path: "/dashboard", label: "Dashboard", icon: FileText },
  { path: "/dashboard/shared", label: "Shared with me", icon: Users },
  { path: "/dashboard/recent", label: "Recent", icon: Clock },
];

function SidebarNavLink({
  item,
  collapsed,
  isActive,
}: {
  item: (typeof menuItems)[number];
  collapsed: boolean;
  isActive: boolean;
}) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.path}
      aria-label={collapsed ? item.label : undefined}
      className={cn(
        "flex items-center rounded-lg transition-colors",
        collapsed ? "justify-center py-2" : "gap-3 px-3 py-2",
        isActive
          ? "bg-primary text-primary-foreground"
          : cn("hover:bg-accent hover:text-accent-foreground", collapsed && "text-muted-foreground")
      )}
    >
      <Icon size={20} />
      {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
    </Link>
  );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedFolderId = searchParams.get("folderId");
  const { data, loading, refresh } = useSidebarTree(selectedFolderId);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => readExpandedSidebarNodeIds());
  const [collapsed, setCollapsed] = useState<boolean>(() => readSidebarCollapsed());

  useEffect(() => {
    writeExpandedSidebarNodeIds(expandedIds);
  }, [expandedIds]);

  useEffect(() => {
    writeSidebarCollapsed(collapsed);
  }, [collapsed]);

  const personalFolders = useMemo(
    () => (data?.personal.root ? [data.personal.root] : []),
    [data?.personal.root]
  );

  const toggle = (id: string) => {
    setExpandedIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(id)) {
        nextIds.delete(id);
      } else {
        nextIds.add(id);
      }
      return nextIds;
    });
  };

  const selectFolder = (id: string) => {
    const params = new URLSearchParams(location.search);
    params.set("folderId", id);
    navigate(`/dashboard?${params.toString()}`);
  };

  const copyDriveLink = (rootFolderId: string) => {
    void navigator.clipboard.writeText(
      `${window.location.origin}/dashboard?folderId=${rootFolderId}`
    );
  };

  return (
    <aside
      className={cn(
        "flex shrink-0 flex-col border-r bg-background transition-[width] duration-200 ease-in-out",
        collapsed ? "w-16" : "w-72"
      )}
    >
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b",
          collapsed ? "justify-center px-3" : "justify-between pr-3 pl-6"
        )}
      >
        {!collapsed && <span className="text-lg font-bold">Workith Editor</span>}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={() => setCollapsed((value) => !value)}
          title={collapsed ? "사이드바 펼치기" : "사이드바 접기"}
          aria-label={collapsed ? "사이드바 펼치기" : "사이드바 접기"}
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>

      <TooltipProvider delayDuration={0}>
        <nav
          className={cn(
            "flex-1 overflow-y-auto",
            collapsed ? "space-y-1 px-2 py-4" : "space-y-4 p-4"
          )}
        >
          <div className="space-y-1">
            {menuItems.map((item) => (
              <SidebarNavLink
                key={item.path}
                item={item}
                collapsed={collapsed}
                isActive={location.pathname === item.path && !selectedFolderId}
              />
            ))}
          </div>

          {!collapsed && (
            <>
              <section>
                <div className="mb-2 flex items-center justify-between px-1">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    개인 폴더
                  </h2>
                </div>
                {loading ? (
                  <div className="px-2 py-1 text-xs text-muted-foreground">Loading...</div>
                ) : (
                  <SidebarFolderTree
                    folders={personalFolders}
                    selectedFolderId={selectedFolderId}
                    expandedIds={expandedIds}
                    onToggle={toggle}
                    onSelectFolder={selectFolder}
                  />
                )}
              </section>

              <section>
                <div className="mb-2 flex items-center justify-between px-1">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    공유 드라이브
                  </h2>
                  {data?.canCreateSharedDrive && (
                    <SharedDriveCreateDialog onCreated={refresh}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="공유 드라이브 만들기"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </SharedDriveCreateDialog>
                  )}
                </div>
                <div className="space-y-3">
                  {data?.sharedDrives.map((section) => {
                    const isExpanded = expandedIds.has(section.root.id);
                    const isSelected = selectedFolderId === section.root.id;
                    const hasChildren = section.root.children.length > 0;
                    const ToggleIcon = isExpanded ? ChevronDown : ChevronRight;

                    return (
                      <div key={section.drive.id}>
                        <div
                          className={cn(
                            "group mb-1 flex h-8 items-center gap-1 rounded-md pr-1 text-sm",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "text-foreground hover:bg-accent"
                          )}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 p-0"
                            onClick={() => toggle(section.root.id)}
                            title={isExpanded ? "접기" : "펼치기"}
                          >
                            {hasChildren ? (
                              <ToggleIcon className="h-4 w-4" />
                            ) : (
                              <span className="h-4 w-4" />
                            )}
                          </Button>
                          <button
                            type="button"
                            onClick={() => selectFolder(section.root.id)}
                            className="flex min-w-0 flex-1 items-center gap-2 text-left font-medium"
                            title={section.drive.name}
                          >
                            <HardDrive className="h-4 w-4 shrink-0" />
                            <span className="truncate">{section.drive.name}</span>
                          </button>
                          {getSharedDriveCapabilities(section.drive.user_role).canManage && (
                            <SharedDriveMembersDialog drive={section.drive}>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6 shrink-0 p-0 opacity-0 group-hover:opacity-100",
                                  isSelected && "text-primary-foreground"
                                )}
                                title="멤버 관리"
                              >
                                <Users className="h-3.5 w-3.5" />
                              </Button>
                            </SharedDriveMembersDialog>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-6 w-6 shrink-0 p-0 opacity-0 group-hover:opacity-100",
                              isSelected && "text-primary-foreground"
                            )}
                            onClick={() => copyDriveLink(section.root.id)}
                            title="공유 드라이브 링크 복사"
                          >
                            <LinkIcon className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {isSelected && (
                          <SidebarDocumentPreviewList documents={section.root.documents ?? []} />
                        )}
                        {isExpanded && hasChildren && (
                          <SidebarFolderTree
                            folders={section.root.children}
                            selectedFolderId={selectedFolderId}
                            expandedIds={expandedIds}
                            onToggle={toggle}
                            onSelectFolder={selectFolder}
                            depth={1}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </nav>
      </TooltipProvider>
    </aside>
  );
}
